import { EdugramIconNew } from '@public/static/icons/edugramIcon'
import { useEffect } from 'react'
import styled from '@emotion/styled'
import { fontFamilies } from '@configs/styles/config'
import { Button } from '@nextui-org/react-latest'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@contexts/Auth'
import { useRouter } from 'next/router'
import Image from 'next/image'

type Props = {
  handleBecomeCreator: () => void
  title?: string
  description?: string
}

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 712px;
  position: relative;

  padding: 4rem 0 3rem 0;
  @media (min-width: 640px) {
    padding: 4rem 0 0 0;
  }
`

const ContentWrapper = styled.div`
  max-width: 1410px;
  margin: 0 auto;
  padding: 0 0.65rem;
  text-align: center;
  position: relative;
  z-index: 1;
`

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
`

const PlatformText = styled.span`
  color: #1a1e3d;
  font-family: ${fontFamilies.regular};
  font-size: 18.8095px;
  flex: none;
  order: 1;
  letter-spacing: 1px;
  flex-grow: 0;
`

const MainHeading = styled.h1`
  font-size: 3rem;
  font-family: ${fontFamilies.bold};
  color: #1a1e3d;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  max-width: 1118px;

  @media (min-width: 768px) {
    font-size: 4.5rem;
  }
`

const SubHeading = styled.p`
  font-size: 24px;
  color: #374151;
  margin-bottom: 2rem;
  max-width: 774px;
  font-weight: ${fontFamilies.medium};
  margin-left: auto;
  margin-right: auto;
  line-height: 1.25;
  @media (min-width: 640px) {
    line-height: 1.75;
  }
`

const ImageContainer = styled.div`
  position: relative;
  /* z-index: 1; */
  margin-top: 6rem;
  display: none;
  margin-bottom: -6.2%;
  @media (min-width: 640px) {
    display: block;
  }
`

const Gradient = styled.div`
  background: radial-gradient(50% 50% at 50% 50%, #fbf5ff 0%, #ffffff 100%);
  border-radius: 50%;
  position: absolute;
  max-width: 976px;
  height: 100%;
  @media (min-width: 640px) {
    height: 976px;
  }
  width: 100%;
  background: radial-gradient(50% 50% at 50% 50%, #fbf5ff 0%, #ffffff 100%);
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 0;
`

const HeroSection = ({ handleBecomeCreator, title, description }: Props) => {
  const { t } = useTranslation('resources/onlineCourses')
  const { push, query } = useRouter()
  const { user } = useAuth()

  useEffect(() => {
    if (!user) return
    if (user?.isAuthor && query.signin === 'true') {
      push('/author?tab=courses')
    } else if (query.signin === 'true') {
      handleBecomeCreator()
      push('?signin=true')
    }
  }, [query, user])

  return (
    <HeroContainer>
      <Gradient />
      <ContentWrapper>
        {/* Logo and platform text */}
        <LogoSection>
          <span className="mb-[-1.4px]">
            <EdugramIconNew
              iconProps={{ width: '28', height: '28' }}
              textProps={{
                fontSize: '18.8px',
                y: '1.03em',
                x: '1.65em',
              }}
              height="28"
              width="116"
            />
          </span>
          <PlatformText>{t('platformText')}</PlatformText>
        </LogoSection>

        {/* Main heading */}
        <MainHeading>{title || t('mainHeading')}</MainHeading>

        {/* Subheading */}
        <SubHeading>{description || t('subHeading')}</SubHeading>
        <Button
          className="py-9 px-12 text-base font-medium"
          color="primary"
          radius="full"
          onPress={() => {
            if (user?.isAuthor) {
              push('/author?tab=courses')
            } else {
              handleBecomeCreator()
              push('?signin=true')
            }
          }}
        >
          {t('createNow')}
        </Button>
      </ContentWrapper>
      <ImageContainer>
        <Image
          src="/static/images/features/student-stats.png"
          width={1100}
          height={500}
          className="w-full"
          alt="Student statistics"
        />
      </ImageContainer>
    </HeroContainer>
  )
}

export default HeroSection
